import log from 'loglevel'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  Snackbar,
  Alert,
  AlertTitle
} from '@mui/material'
import {
  Form,
  Loading,
  useDataProvider,
  useGetOne,
  useInput,
  useTranslate
} from 'react-admin'
import { useParams } from 'react-router-dom'
import React, { useState, type SetStateAction } from 'react'
import { isNil, map, replace } from 'lodash/fp'
import { useFormContext, useFormState } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import { type GmoBankAccount } from './types'
import { TextInput, ValidateError } from '../Util/index'
import { type EntryUserData } from '../../../../cdk/bapp/lib/lambda-api/src/types'

type SelectItem = {
  value: string
  name: string
}

log.setLevel(log.levels.DEBUG)

const schema = z.object({
  bankAccount: z.object({
    bankCode: z
      .string({ message: 'required' })
      .min(4, { message: 'too small' })
      .max(4, { message: 'too large' }),
    branchCode: z
      .string({ message: 'required' })
      .min(3, { message: 'too small' })
      .max(3, { message: 'too large' }),
    accountType: z
      .string({ message: 'required' })
      .min(1, { message: 'required' }),
    accountNumber: z
      .string({ message: 'required' })
      .min(7, { message: 'too small' })
      .max(7, { message: 'too large' }),
    accountName: z
      .string({ message: 'required' })
      .min(1, { message: 'required' })
      .regex(/^[\u30A0-\u30FF]+$/, { message: 'must kana' }),
    accountNameKanji: z
      .string({ message: 'required' })
      .min(1, { message: 'required' })
  })
})
const SelectInput: React.FC<{
  source: string
  choices: SelectItem[]
}> = ({ source, choices, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { field, isRequired } = useInput({ source, ...props })
  const translate = useTranslate()
  const label = translate(`resources.orderR.fields.${field.name}`)
  const { errors } = useFormState()
  return (
    <>
      <Typography variant="subtitle1">{label}</Typography>
      <Box
        {...props}
        border={0}
        sx={{
          height: '1.8em',
          display: 'flex',
          alignContent: 'center',
          marginTop: isDesktop ? '0.1em' : '0.25em',
          marginLeft: isDesktop ? '0.1em' : '0.25em',
          borderRadius: '10px',
          borderColor: '#c5c5c5'
        }}>
        <Select
          {...field}
          fullWidth
          sx={{
            marginTop: '0.2em',
            marginLeft: '0.5em',
            fontSize: '15px',
            borderRadius: '10px'
          }}
          required={isRequired}>
          {map((item: SelectItem) => (
            <MenuItem value={item.value}>
              {translate(`resources.orderR.fields.${item.name}`)}
            </MenuItem>
          ))(choices)}
        </Select>
      </Box>
      {!isNil(errors[source]?.message) && (
        <ValidateError errorMessage={errors[source].message.toString()} />
      )}
    </>
  )
}
const BankAccountDialog: React.FC<
  GmoBankAccount & {
    open: boolean
  }
> = ({ TranID, Token, StartUrl, open }) => {
  const translate = useTranslate()
  // const [disable, setDisable] = React.useState(false)
  // const onSubmit = (): void => {
  //   log.debug('[BankAccountDialog]', { TranID, Token })
  //   setDisable(true)
  // }
  return (
    <Dialog open={open}>
      <form action={StartUrl} method="POST">
        <DialogContent>
          {translate('resources.orderR.fields.bankAccount.dialog.content')}
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            // disabled={disable}
            sx={{
              borderRadius: '40px',
              height: '3em'
            }}>
            <Typography>
              {translate('resources.orderR.fields.bankAccount.dialog.ok')}
            </Typography>
          </Button>
        </DialogActions>
        <input type="hidden" name="TranID" value={TranID} />
        <input type="hidden" name="Token" value={replace(/ /g, '+')(Token)} />
      </form>
    </Dialog>
  )
}
const SubmitButton: React.FC<{
  id: string
  setGmoBankAccount: React.Dispatch<SetStateAction<GmoBankAccount | undefined>>
  setOpenAlert: React.Dispatch<SetStateAction<boolean>>
}> = ({ id, setGmoBankAccount, setOpenAlert }) => {
  const translate = useTranslate()
  const [enable, setEnable] = React.useState(false)
  const { getValues } = useFormContext()
  const { errors, isValid } = useFormState()
  const dataProvider = useDataProvider()
  const onClick = (): void => {
    setEnable(false)
    const { bankAccount } = getValues()
    const data = {
      user: id,
      bankCode: bankAccount.bankCode,
      branchCode: bankAccount.branchCode,
      accountType: bankAccount.accountType,
      accountNumber: bankAccount.accountNumber,
      accountName: bankAccount.accountName,
      accountNameKanji: bankAccount.accountNameKanji
    }
    dataProvider
      .create('gmopg/bankaccount', { data })
      .then((result) => {
        setGmoBankAccount(result.data)
      })
      .catch((error) => {
        log.error(error)
        setEnable(true)
        setGmoBankAccount(undefined)
        setOpenAlert(true)
      })
  }
  React.useEffect(() => {
    const { ...record } = getValues()
    schema.safeParse(record)
    setEnable(isValid)
  }, [errors, isValid])
  return (
    <Button
      variant="contained"
      color="primary"
      onClick={onClick}
      disabled={!enable}
      sx={{
        borderRadius: '40px',
        height: '3em'
      }}>
      {translate('resources.orderR.buttons.submit')}
    </Button>
  )
}
const BackAccountFormContent: React.FC<{
  id: string
  setGmoBankAccount: React.Dispatch<SetStateAction<GmoBankAccount | undefined>>
}> = ({ id, setGmoBankAccount }) => {
  const translate = useTranslate()
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { data: user, isLoading: isLoadingUser } = useGetOne<
    EntryUserData & { id: string }
  >('users', {
    id
  })
  const [openAlert, setOpenAlert] = useState<boolean>(false)
  const handleAlertClose = (): void => {
    setOpenAlert(false)
  }
  if (isLoadingUser) return <Loading />
  if (isNil(user)) return <></>
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '1280px' }}>
        <Box sx={{ marginLeft: '0.5em' }}>
          <picture>
            <img src="/siteLogoR.svg" width="323px" alt="terashite R" />
          </picture>
        </Box>
        <Box sx={{ backgroundColor: '#fff7e1' }}>
          <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
            <Grid item xs={12} sx={{ marginTop: '2em' }}>
              <Grid container>
                <Grid item xs={12} display={'flex'} justifyContent={'center'}>
                  <Typography
                    variant="h5"
                    sx={{ marginTop: '1.5em', marginBottom: '1em' }}>
                    {translate('resources.orderR.fields.bankAccount.title')}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: '-1em',
                    mb: '2em'
                  }}>
                  <picture>
                    <img src="/bar.png" alt="bar" />
                  </picture>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={isDesktop ? 6 : 12}>
                  <TextInput
                    fields={'resources.orderR.fields'}
                    source="bankAccount.bankCode"
                    placeholder="0000"
                  />
                </Grid>
                <Grid item xs={isDesktop ? 6 : 12}>
                  <TextInput
                    fields={'resources.orderR.fields'}
                    source="bankAccount.branchCode"
                    placeholder="000"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={isDesktop ? 6 : 12}>
                  <SelectInput
                    source="bankAccount.accountType"
                    choices={[
                      { value: '1', name: 'savings' },
                      { value: '2', name: 'current' }
                    ]}
                  />
                </Grid>
                <Grid item xs={isDesktop ? 6 : 12}>
                  <TextInput
                    fields={'resources.orderR.fields'}
                    source="bankAccount.accountNumber"
                    placeholder="0123456"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={isDesktop ? 6 : 12}>
                  <TextInput
                    fields={'resources.orderR.fields'}
                    source="bankAccount.accountName"
                    placeholder="チュウデンタロウ"
                  />
                </Grid>
                <Grid item xs={isDesktop ? 6 : 12}>
                  <TextInput
                    fields={'resources.orderR.fields'}
                    source="bankAccount.accountNameKanji"
                    placeholder="中電太郎"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Snackbar
              open={openAlert}
              autoHideDuration={5000}
              onClose={handleAlertClose}>
              <Alert severity="error">
                <AlertTitle>{'口座登録エラー'}</AlertTitle>
                {'ご入力いただいた銀行コードもしくは支店コードは存在しません。'}
              </Alert>
            </Snackbar>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '1.5em',
                paddingBottom: '2.5em'
              }}>
              <SubmitButton
                id={id}
                setGmoBankAccount={setGmoBankAccount}
                setOpenAlert={setOpenAlert}
              />
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const BankAccountForm: React.FC = () => {
  const params = useParams()
  const id = params['id'] as string
  const [gmoBankAccount, setGmoBankAccount] = React.useState<
    GmoBankAccount | undefined
  >(undefined)
  return !isNil(gmoBankAccount) ? (
    <BankAccountDialog
      TranID={gmoBankAccount.TranID}
      Token={gmoBankAccount.Token}
      StartUrl={gmoBankAccount.StartUrl}
      open
    />
  ) : (
    <Form resolver={zodResolver(schema)} mode="onBlur" reValidateMode="onBlur">
      <BackAccountFormContent id={id} setGmoBankAccount={setGmoBankAccount} />
    </Form>
  )
}
export const BankAccountCompleteForm: React.FC = () => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '1280px' }}>
        <Box sx={{ marginLeft: '0.5em' }}>
          <picture>
            <img src="/siteLogoR.svg" width="323px" alt="terashite R" />
          </picture>
        </Box>
        <Box
          sx={{
            backgroundColor: '#fff7e1',
            paddingTop: '2em',
            paddingBottom: '3em'
          }}>
          <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
            <Grid item xs={12} sx={{ marginTop: '2em' }}>
              <Grid container>
                <Grid item xs={12} display={'flex'} justifyContent={'center'}>
                  <Typography
                    variant="h5"
                    sx={{ marginTop: '1.5em', marginBottom: '1em' }}>
                    {'口座登録受付完了'}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: '-1em',
                    mb: '2em'
                  }}>
                  <picture>
                    <img src="/bar.png" alt="bar" />
                  </picture>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                marginTop: '0.1em'
              }}>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography
                  variant="h6"
                  sx={{
                    marginTop: '0em',
                    whiteSpace: 'pre-wrap'
                  }}>
                  {`お申し込みありがとうございます。ご入力いただいた内容をもとに、口座登録を受け付けました。`}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
